import React from 'react';
import { useTranslation } from 'react-i18next';
import { sortBy, isFunction } from 'lodash';
import { Button, Flex, Tag, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/pro-light-svg-icons';

import Weight from 'components/controls/Weight';
import { getItemAlteredByScenario } from 'utils/rules';
import {
	FishboneLinkTarget,
	useFishboneLinkController
} from 'hooks/useFishboneLinkController';
import './FishboneLinksTab.sass';

interface FishboneLinksTabProps {
	// L'objet cible selectionne (risk / stake / indicator), porteur de fishboneLinks
	selection: any;
	// Type de cible -> selectionne le bon jeu de mutations
	targetType: FishboneLinkTarget;
	// Arbre fishbone complet (branches + items)
	fishboneBranchs: any[];
	scenario: any;
	select?: Function;
	onClose?: Function;
}

// Onglet "Fishbone" cote objet cible : liste toutes les branches puis leurs
// sous-aretes, montre celles liees a l'objet et permet de (de)lier + cycler le
// poids 1->2->3. Reutilise le controller symetrique (create*Link prend
// fishboneBranchId/fishboneItemId + <target>Id).
export const FishboneLinksTab = (props: FishboneLinksTabProps) => {
	const { t } = useTranslation();
	const { createLink, deleteLink, updateLinkStrength } =
		useFishboneLinkController();

	const links: any[] = props.selection?.fishboneLinks || [];
	const linkByNode = (nodeId: string) =>
		links.find((l: any) => l && l.fishboneNodeId === nodeId);

	const setLinks = (next: any[]) => {
		if (typeof props.select === 'function')
			props.select({ ...props.selection, fishboneLinks: next });
	};

	// Sous-titre de type, comme l'en-tete des autres onglets
	const typeLabel =
		props.targetType === 'stake'
			? t('models.stake.name')
			: props.targetType === 'indicator'
			? t('models.indicator.name')
			: props.selection?.isOpportunity
			? t('models.risk.name_positive')
			: t('models.risk.name');

	const branches = sortBy(
		(props.fishboneBranchs || [])
			.map((b: any) => getItemAlteredByScenario(b, props.scenario))
			.filter((b: any) => b.enabled !== false),
		'name'
	);

	const onToggle = async (node: any, nodeType: 'branch' | 'item') => {
		const link = linkByNode(node.id);
		if (!link || !link.id) {
			const created = await createLink(
				props.targetType,
				nodeType,
				node.id,
				props.selection?.id
			);
			if (created) setLinks([...links, created]);
		} else if (link.strength < 3) {
			await updateLinkStrength(props.targetType, link, link.strength + 1);
			setLinks(
				links.map((l: any) =>
					l.id === link.id ? { ...l, strength: l.strength + 1 } : l
				)
			);
		} else {
			await deleteLink(
				props.targetType,
				link.id,
				nodeType,
				node.id,
				props.selection?.id
			);
			setLinks(links.filter((l: any) => l.id !== link.id));
		}
	};

	const renderChip = (node: any, nodeType: 'branch' | 'item') => {
		const link = linkByNode(node.id);
		return (
			<Tag.CheckableTag
				key={node.id}
				checked={!!link}
				onClick={() => onToggle(node, nodeType)}
				className="ImpactedRisk tag"
			>
				<span
					className="tag__label"
					title={node.name}
					style={nodeType === 'branch' ? { fontWeight: 600 } : undefined}
				>
					{node.name}
				</span>
				{link ? <Weight onClick={() => {}}>{link.strength}</Weight> : null}
			</Tag.CheckableTag>
		);
	};

	return (
		<Flex vertical className="FishboneLinksTab">
			<Flex className="mb-1">
				<div className="w-100 text-left">
					<div className="title">{props.selection?.name}</div>
					<small>{typeLabel}</small>
				</div>
				{isFunction(props.onClose) && (
					<Tooltip title={t('actions.close')} placement="left">
						<Button
							shape="circle"
							variant="solid"
							className="mini ml-auto"
							style={{ marginRight: '-0.9rem' }}
							onClick={() => props.onClose!()}
						>
							<FontAwesomeIcon icon={faClose} />
						</Button>
					</Tooltip>
				)}
			</Flex>
			{branches.length ? (
				branches.map((branch: any) => {
					const items = sortBy(
						(branch.items || [])
							.map((i: any) =>
								getItemAlteredByScenario(i, props.scenario)
							)
							.filter((i: any) => i.enabled !== false),
						'name'
					);
					return (
						<div key={branch.id} className="mb-1 text-left">
							{renderChip(branch, 'branch')}
							{items.length ? (
								<div style={{ marginLeft: '1rem' }}>
									{items.map((item: any) =>
										renderChip(item, 'item')
									)}
								</div>
							) : null}
						</div>
					);
				})
			) : (
				<div
					className="text-center"
					style={{ color: 'var(--ant-color-text-tertiary)' }}
				>
					{t('messages.not_defined')}
				</div>
			)}
		</Flex>
	);
};

export default FishboneLinksTab;
